import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { createScorm12Api, createScorm2004Api } from '../../../../utils/scormApi';
import { launchScormAttempt, saveScormProgress } from '../../../../services/userService';

function ScormPlayer({ scormContentId, title, onComplete, currentLessonIndex, totalLessons, onNextLesson }) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const completedRef = useRef(false);
  const [launchData, setLaunchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Keep onComplete in a ref so the effect never re-runs when the parent re-renders
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    let isMounted = true;

    const launch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await launchScormAttempt(scormContentId);

        if (!response?.success) {
          throw new Error(response?.data?.error_msg || 'Failed to launch SCORM content');
        }

        const data = response.data;
        if (!isMounted) return;

        const handleCommit = async (summary, snapshot = {}) => {
          const lessonStatus = summary?.status || snapshot['cmi.core.lesson_status'] || snapshot['cmi.completion_status'] || 'incomplete';
          const score = summary?.score || snapshot['cmi.core.score.raw'] || snapshot['cmi.score.raw'] || '';
          const lessonLocation = snapshot['cmi.core.lesson_location'] || snapshot['cmi.location'] || '';
          const suspendData = snapshot['cmi.suspend_data'] || '';
          const totalTime = snapshot['cmi.core.total_time'] || snapshot['cmi.total_time'] || '';

          await saveScormProgress({
            scorm_content_id: scormContentId,
            attempt_id: data.attempt_id,
            lesson_status: lessonStatus,
            score,
            lesson_location: lessonLocation,
            suspend_data: suspendData,
            total_time: totalTime,
            runtime_data: JSON.stringify(snapshot)
          });

          if (['passed', 'completed'].includes(String(lessonStatus).toLowerCase()) && !completedRef.current) {
            completedRef.current = true;
            setCompleted(true);
            onCompleteRef.current?.(scormContentId);
          }
        };

        const runtimeOrigin = new URL(data.scorm_runtime_url).origin;
        const handleRuntimeMessage = (event) => {
          if (event.origin !== runtimeOrigin) return;
          if (event.data?.source !== 'gomedulla-scorm-runtime') return;
          if (event.data?.attempt_id !== data.attempt_id) return;
          if (event.data?.type === 'commit') {
            handleCommit(event.data.summary, event.data.runtime_data).catch((err) => {
              toast.error(err.response?.data?.data?.error_msg || err.message || 'Failed to save SCORM progress', { position: 'top-center' });
            });
          }
        };

        if (window.__gomedullaScormMessageHandler) {
          window.removeEventListener('message', window.__gomedullaScormMessageHandler);
        }
        window.__gomedullaScormMessageHandler = handleRuntimeMessage;
        window.addEventListener('message', handleRuntimeMessage);

        const { api: scorm12Api } = createScorm12Api({
          attemptId: data.attempt_id,
          initialRuntimeData: data.runtime_data || {},
          scormRuntimeUrl: data.scorm_runtime_url,
          onCommit: handleCommit,
          onLog: () => {}
        });

        const { api: scorm2004Api } = createScorm2004Api({
          attemptId: data.attempt_id,
          initialRuntimeData: data.runtime_data || {},
          scormRuntimeUrl: data.scorm_runtime_url,
          onCommit: handleCommit,
          onLog: () => {}
        });

        window.API = scorm12Api;
        window.API_1484_11 = scorm2004Api;

        setLaunchData(data);
      } catch (err) {
        if (!isMounted) return;
        const message = err.response?.data?.data?.error_msg || err.message || 'Failed to launch SCORM content';
        setError(message);
        toast.error(message, { position: 'top-center' });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    launch();

    return () => {
      isMounted = false;
      if (window.__gomedullaScormMessageHandler) {
        window.removeEventListener('message', window.__gomedullaScormMessageHandler);
        delete window.__gomedullaScormMessageHandler;
      }
      delete window.API;
      delete window.API_1484_11;
    };
  }, [scormContentId]);

  if (loading) {
    return (
      <div className="min-h-[420px] flex flex-col items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#7083FF]" role="status" aria-label="Loading SCORM content" />
        <p className="mt-4 text-sm font-medium text-gray-500">Launching SCORM content...</p>
      </div>
    );
  }

  if (error || !launchData?.launch_url) {
    return (
      <div className="min-h-[420px] flex flex-col items-center justify-center bg-white p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <Icon icon="fluent:error-circle-24-regular" width={28} height={28} className="text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to launch SCORM content</h3>
        <p className="text-sm text-gray-500 max-w-md">{error || 'Launch URL is missing.'}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white flex flex-col" style={isFullscreen ? { height: '100vh' } : {}}>
      <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium text-[#7083FF] mb-1">
            <Icon icon="fluent:archive-24-regular" width={16} height={16} />
            <span>SCORM Lesson</span>
            {currentLessonIndex && totalLessons ? <span>· Lesson {currentLessonIndex} of {totalLessons}</span> : null}
          </div>
          <h2 className="text-lg font-semibold text-gray-900 truncate">{title || launchData.title || 'SCORM Content'}</h2>
        </div>
        <div className="flex items-center gap-2">
          {completed && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-100">
              <Icon icon="fluent:checkmark-circle-24-filled" width={16} height={16} />
              Completed
            </div>
          )}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-100 transition"
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            <Icon icon={isFullscreen ? 'fluent:full-screen-minimize-24-regular' : 'fluent:full-screen-maximize-24-regular'} width={16} height={16} />
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
        </div>
      </div>
      <iframe
        ref={iframeRef}
        src={launchData.launch_url}
        title={title || 'SCORM Content'}
        className="w-full border-0 bg-white"
        style={{ height: isFullscreen ? 'calc(100vh - 130px)' : '72vh' }}
        allow="fullscreen"
      />
      <div className="px-5 py-4 border-t border-gray-100 flex justify-end bg-gray-50">
        <button type="button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7083FF] text-white font-medium text-sm hover:bg-[#5c6ef0] disabled:opacity-50" onClick={onNextLesson} disabled={!completed}>
          Next Lesson
          <Icon icon="mdi:arrow-right" width={18} height={18} />
        </button>
      </div>
    </div>
  );
}

export default ScormPlayer;
